import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Loader2, FileText, Download, Mail } from "lucide-react"
import { downloadFile } from "@/lib/download"
import type { CourrierTemplate } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedProjectIds: string[]
  templates: CourrierTemplate[]
}

const PLACEHOLDERS = [
  { category: "Propriétaire", items: ["%owner.fullName%", "%owner.firstName%", "%owner.lastName%", "%owner.gender%", "%owner.address%", "%owner.zipCode%", "%owner.cityName%"] },
  { category: "Projet", items: ["%project.name%", "%project.address%", "%project.zip_code%", "%project.city_name%", "%project.property_type%"] },
  { category: "Utilisateur", items: ["%user.first_name%", "%user.last_name%", "%user.email_address%", "%user.phone_number%", "%user.job_title%"] },
]

export function CourrierDialog({ open, onOpenChange, selectedProjectIds, templates }: Props) {
  const [mode, setMode] = useState<"template" | "editor">(templates.length > 0 ? "template" : "editor")
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [editorBody, setEditorBody] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId)

  const handleGeneratePdf = async () => {
    const templateId = selectedTemplateId
    if (!templateId) return

    setLoading(true)
    setError(null)
    try {
      await downloadFile("/courriers/generate_pdf", {
        project_ids: selectedProjectIds,
        template_id: templateId,
      }, "courrier.pdf")
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de la génération")
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadCsv = async () => {
    setLoading(true)
    setError(null)
    try {
      await downloadFile("/courriers/generate_csv", {
        project_ids: selectedProjectIds,
      }, "maileva-destinataires.csv")
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors du téléchargement")
    } finally {
      setLoading(false)
    }
  }

  const insertPlaceholder = (placeholder: string) => {
    setEditorBody((prev) => prev + placeholder)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            Générer un courrier
            <Badge variant="secondary">{selectedProjectIds.length} projet{selectedProjectIds.length > 1 ? "s" : ""}</Badge>
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            {templates.length > 0 && (
              <Button
                variant={mode === "template" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("template")}
              >
                Modèles
              </Button>
            )}
            <Button
              variant={mode === "editor" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("editor")}
            >
              Éditeur
            </Button>
          </div>

          {/* Template selection mode */}
          {mode === "template" && (
            <div className="grid gap-2">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className={`cursor-pointer transition-colors ${
                    selectedTemplateId === template.id
                      ? "border-primary bg-primary/5"
                      : "hover:border-muted-foreground/30"
                  }`}
                  onClick={() => setSelectedTemplateId(template.id)}
                >
                  <CardContent className="flex items-center gap-3 py-3">
                    <FileText className="text-muted-foreground size-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{template.title}</p>
                      {template.body && (
                        <p className="text-muted-foreground text-sm truncate">{template.body.slice(0, 100)}...</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {templates.length === 0 && (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  Aucun modèle de courrier configuré. Ajoutez-en dans les paramètres de votre organisation.
                </p>
              )}
            </div>
          )}

          {/* Editor mode */}
          {mode === "editor" && (
            <div className="space-y-3">
              <div>
                <Label>Corps du message</Label>
                <Textarea
                  value={editorBody}
                  onChange={(e) => setEditorBody(e.target.value)}
                  placeholder="Rédigez votre courrier ici. Utilisez les variables ci-dessous pour personnaliser le contenu."
                  rows={8}
                  className="mt-1"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Variables disponibles</Label>
                {PLACEHOLDERS.map((group) => (
                  <div key={group.category}>
                    <span className="text-xs font-medium text-muted-foreground">{group.category}</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {group.items.map((placeholder) => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder)}
                          className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono hover:bg-muted-foreground/20 transition-colors"
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </DialogBody>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleDownloadCsv} disabled={loading}>
            <Download className="size-4" />
            CSV Maileva
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleGeneratePdf}
            disabled={loading || !selectedTemplateId}
          >
            {loading && <Loader2 className="size-4 animate-spin" />}
            Générer le courrier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
